import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  FileSearch,
  History,
  Repeat2,
  ScrollText,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import {
  evaluateGuidedBuying,
  guidedBuyingWorkspace,
  recordGuidedBuyingDecision,
  reviewGuidedBuyingException,
} from "@/lib/api";
import type { GuidedBuyingWorkspace } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { displayDate, money, titleCase } from "@/lib/utils";

type Props = {
  organisationId: string;
  requisitionId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEvaluate: boolean;
  canReviewException: boolean;
  onChanged: () => void;
};

type AlternativeGroup = {
  key: string;
  title: string;
  description: string;
  action: string;
  sourceKind: string;
  idKey: string;
  button: string;
  icon: typeof Boxes;
};

const alternativeGroups: AlternativeGroup[] = [
  {
    key: "inventory_available",
    title: "Available inventory",
    description: "Use stock already held by the organisation instead of buying again.",
    action: "route_inventory",
    sourceKind: "inventory_item",
    idKey: "inventory_item_id",
    button: "Route to inventory",
    icon: Boxes,
  },
  {
    key: "active_contracts",
    title: "Active contracts and rates",
    description: "Route the need through an existing contracted commercial path.",
    action: "route_contract",
    sourceKind: "contract_item",
    idKey: "contract_item_id",
    button: "Use contracted route",
    icon: ScrollText,
  },
  {
    key: "active_coverage",
    title: "Warranty, AMC or guarantee",
    description: "Raise the need against active coverage before purchasing a replacement.",
    action: "route_coverage",
    sourceKind: "asset",
    idKey: "asset_id",
    button: "Use coverage route",
    icon: Wrench,
  },
  {
    key: "duplicate_subscriptions",
    title: "Existing subscriptions",
    description: "Use an active entitlement rather than creating duplicate spend.",
    action: "use_existing_subscription",
    sourceKind: "recurring_commitment",
    idKey: "commitment_id",
    button: "Use existing entitlement",
    icon: Repeat2,
  },
  {
    key: "lower_historic_prices",
    title: "Historic price benchmarks",
    description: "Carry forward the recorded lower-price benchmark into sourcing.",
    action: "use_historic_price",
    sourceKind: "purchase_order",
    idKey: "purchase_order_id",
    button: "Use price benchmark",
    icon: History,
  },
];

function alternatives(
  workspace: GuidedBuyingWorkspace | undefined,
  group: AlternativeGroup,
) {
  const value = workspace?.evaluation?.checks?.[group.key];
  return Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
}

function detailValue(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return String(value);
  return String(value);
}

function AlternativeCard({
  group,
  item,
  busy,
  disabled,
  onChoose,
}: {
  group: AlternativeGroup;
  item: Record<string, unknown>;
  busy: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  const Icon = group.icon;
  const excluded = new Set([
    "requisition_line_id",
    group.idKey,
    "contract_id",
    "coverage_id",
    "po_line_id",
    "location_id",
  ]);
  return (
    <article className="rounded-xl border border-[var(--border)] bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[.1em] text-[var(--text-muted)]">
            {detailValue(item.requisition_line)}
          </p>
          <strong className="mt-1 block text-sm">
            {detailValue(item.item || item.asset || item.title || item.po_no)}
          </strong>
        </div>
        <Icon className="size-5 text-[var(--teal)]" />
      </div>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
        {Object.entries(item)
          .filter(([key]) => !excluded.has(key) && key !== "requisition_line")
          .slice(0, 8)
          .map(([key, value]) => (
            <div key={key}>
              <dt className="text-[var(--text-muted)]">{titleCase(key)}</dt>
              <dd className="font-medium text-[var(--text-secondary)]">
                {detailValue(value)}
              </dd>
            </div>
          ))}
      </dl>
      <Button
        className="mt-4"
        size="sm"
        variant="outline"
        disabled={busy || disabled}
        onClick={onChoose}
      >
        {group.button}
      </Button>
    </article>
  );
}

export function GuidedBuyingDialog({
  organisationId,
  requisitionId,
  open,
  onOpenChange,
  canEvaluate,
  canReviewException,
  onChanged,
}: Props) {
  const [data, setData] = useState<GuidedBuyingWorkspace>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    if (!requisitionId) return;
    setLoading(true);
    setError("");
    try {
      setData(await guidedBuyingWorkspace(organisationId, requisitionId));
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setLoading(false);
    }
  }, [organisationId, requisitionId]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [load, open]);

  const hasAlternatives = useMemo(
    () => alternativeGroups.some((group) => alternatives(data, group).length > 0),
    [data],
  );
  const evaluation = data?.evaluation;
  const state = data?.requisition.guided_buying_state || "not_reviewed";
  const isDraft = data?.requisition.status === "draft";
  const canChoose = isDraft && canEvaluate && state === "review_required";
  const readyToSubmit = ["clear", "warning_acknowledged", "exception_approved"].includes(state);

  async function evaluate() {
    if (!requisitionId) return;
    setBusy("evaluate");
    setError("");
    try {
      setData(await evaluateGuidedBuying(organisationId, requisitionId));
      onChanged();
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function decide(
    decision: string,
    sourceKind?: string,
    sourceId?: string,
  ) {
    if (!requisitionId || !evaluation) return;
    setBusy(`${decision}:${sourceId || "none"}`);
    setError("");
    try {
      await recordGuidedBuyingDecision(organisationId, requisitionId, evaluation.id, {
        decision,
        sourceKind,
        sourceId,
        reason,
      });
      setReason("");
      await load();
      onChanged();
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function review(decision: "approve_exception" | "deny_exception") {
    if (!requisitionId || !evaluation) return;
    setBusy(decision);
    setError("");
    try {
      await reviewGuidedBuyingException(
        organisationId,
        requisitionId,
        evaluation.id,
        decision,
        reason,
      );
      setReason("");
      await load();
      onChanged();
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy("");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setReason("");
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-h-[92vh] w-[min(1080px,calc(100vw-2rem))] overflow-y-auto">
        <DialogHeader
          eyebrow="GUIDED BUYING"
          title="Check before you buy"
          description="RAQOZ checks stock, contracts, coverage, subscriptions and recorded prices. You choose the route; the system preserves the evidence."
        />

        {loading && <div className="p-6 text-sm text-[var(--text-secondary)]">Loading the governed buying review…</div>}
        {error && <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">{error}</div>}

        {!loading && data && (
          <div className="grid gap-5 p-5">
            <section className="grid gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-secondary)] p-5 sm:grid-cols-[1fr_auto]">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <strong>{data.requisition.request_no}</strong>
                  <Badge value={state} />
                  {evaluation && <Badge value={evaluation.decision} />}
                </div>
                <h2 className="mt-2 text-xl font-semibold">{data.requisition.title}</h2>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Needed {displayDate(data.requisition.needed_by)} · {money(data.requisition.estimated_value)} · {data.lines.length} line{data.lines.length === 1 ? "" : "s"}
                </p>
                {evaluation?.blocking_reason && (
                  <p className="mt-3 flex items-start gap-2 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    {evaluation.blocking_reason}
                  </p>
                )}
              </div>
              {isDraft && canEvaluate && (!evaluation || ["review_required", "exception_denied"].includes(state)) && (
                <Button variant={evaluation ? "outline" : "default"} disabled={busy === "evaluate"} onClick={evaluate}>
                  <FileSearch className="size-4" />
                  {busy === "evaluate" ? "Checking…" : evaluation ? "Refresh checks" : "Check alternatives"}
                </Button>
              )}
            </section>

            {evaluation && (
              <>
                {hasAlternatives ? (
                  alternativeGroups.map((group) => {
                    const rows = alternatives(data, group);
                    if (!rows.length) return null;
                    const Icon = group.icon;
                    return (
                      <section key={group.key} className="surface-subtle overflow-hidden">
                        <header className="section-heading">
                          <span className="section-heading__icon"><Icon className="size-4" /></span>
                          <div><h2>{group.title}</h2><p>{group.description}</p></div>
                        </header>
                        <div className="grid gap-3 p-4 md:grid-cols-2">
                          {rows.map((item, index) => {
                            const sourceId = String(item[group.idKey] || "");
                            return (
                              <AlternativeCard
                                key={`${group.key}:${sourceId}:${index}`}
                                group={group}
                                item={item}
                                busy={Boolean(busy)}
                                disabled={!canChoose}
                                onChoose={() => decide(group.action, group.sourceKind, sourceId)}
                              />
                            );
                          })}
                        </div>
                      </section>
                    );
                  })
                ) : (
                  <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-950">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 size-5" />
                      <div><strong>No recorded alternative was found</strong><p className="mt-1 text-sm">The checks returned no stock, contract, coverage, subscription or lower-price route for the requested lines.</p></div>
                    </div>
                    {canChoose && evaluation.decision === "clear" && (
                      <Button className="mt-4" size="sm" disabled={Boolean(busy)} onClick={() => decide("acknowledge_clear")}>Confirm and continue</Button>
                    )}
                  </section>
                )}

                {canChoose && evaluation.decision === "warn" && (
                  <DecisionReason
                    label="Why should this requirement continue despite the warning?"
                    reason={reason}
                    setReason={setReason}
                  >
                    <Button disabled={Boolean(busy) || reason.trim().length < 20} onClick={() => decide("acknowledge_warning")}>
                      Acknowledge warning and continue
                    </Button>
                  </DecisionReason>
                )}

                {canChoose && evaluation.decision === "block" && (
                  <DecisionReason
                    label="Why is an exception required instead of the available route?"
                    reason={reason}
                    setReason={setReason}
                  >
                    <Button disabled={Boolean(busy) || reason.trim().length < 20} onClick={() => decide("request_exception")}>
                      <ShieldCheck className="size-4" />Request approver exception
                    </Button>
                  </DecisionReason>
                )}

                {state === "exception_requested" && canReviewException && (
                  <DecisionReason
                    label="Record the evidence-based approver decision"
                    reason={reason}
                    setReason={setReason}
                  >
                    <Button disabled={Boolean(busy) || reason.trim().length < 20} onClick={() => review("approve_exception")}>
                      Approve exception
                    </Button>
                    <Button variant="outline" disabled={Boolean(busy) || reason.trim().length < 20} onClick={() => review("deny_exception")}>
                      Deny exception
                    </Button>
                  </DecisionReason>
                )}

                <section className="rounded-2xl border border-[var(--border)] p-5">
                  <div className="flex items-start gap-3">
                    {readyToSubmit ? <CheckCircle2 className="mt-0.5 size-5 text-emerald-700" /> : <ShieldCheck className="mt-0.5 size-5 text-[var(--teal)]" />}
                    <div>
                      <strong>{readyToSubmit ? "Guided review complete" : state === "alternative_selected" ? "Alternative route selected" : "A recorded decision is still required"}</strong>
                      <p className="mt-1 text-sm text-[var(--text-secondary)]">
                        {readyToSubmit
                          ? "The requisition may now be submitted from the requisitions list."
                          : state === "alternative_selected"
                            ? "This need will not enter new sourcing. Follow the selected inventory, contract, coverage or entitlement route."
                            : state === "exception_requested"
                              ? "An approver must decide the exception before submission."
                              : "Choose an available route or record the required acknowledgement."}
                      </p>
                    </div>
                  </div>
                </section>

                {data.decisions.length > 0 && (
                  <section className="rounded-2xl border border-[var(--border)] p-5">
                    <h2 className="font-semibold">Decision evidence</h2>
                    <div className="mt-3 grid gap-3">
                      {data.decisions.map((decision) => (
                        <article key={decision.id} className="rounded-xl bg-[var(--surface-secondary)] p-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <strong>{titleCase(decision.decision)}</strong>
                            <span className="text-xs text-[var(--text-muted)]">{new Date(decision.decided_at).toLocaleString()}</span>
                          </div>
                          {decision.reason && <p className="mt-1 text-[var(--text-secondary)]">{decision.reason}</p>}
                        </article>
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DecisionReason({
  label,
  reason,
  setReason,
  children,
}: {
  label: string;
  reason: string;
  setReason: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <Label>{label}<Textarea className="mt-2 bg-white" value={reason} onChange={(event) => setReason(event.target.value)} minLength={20} placeholder="Record at least 20 characters so the decision is specific and auditable." /></Label>
      <div className="mt-3 flex flex-wrap gap-2">{children}</div>
    </section>
  );
}
